import { useParams, Routes, Route, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ViewDetailsPage from "../app/ADN/ViewDetailsPage/ViewDetailsPage";
import { ResourceManager, EngagementManager } from "../components";
import { routes } from "./routes.constatns";
import AOAResourceManager from "../app/AOA/ResourceManager/ResourceManager";
import AOAEngagementManager from "../app/AOA/EngagementManager/EngagementManager";
import AOAViewDetailsPage from "../app/AOA/ViewDetailsPage/ViewDetailsPage";
import ADNPowerBiDashboard from "../app/ADN/PowerBiDashboard/PowerBiDashboard";
import AOAPowerBiDashboard from "../app/AOA/PowerBiDashboard/PowerBiDashboard";
import { getPowerBiLink } from "../constants/powerBiLinks";
import NoAccess from "../components/NoAccess/NoAccess";
import {
  isEngagementManager as checkIsEngagementManager,
  isResourceManager as checkIsResourceManager,
  isGlobalReporting,
  isGdsEmail,
} from "../util/roleHelpers";

const DomainLayout = () => {
  const { domain } = useParams();
  const roleData = useSelector((state) => state.landing.roleData);
  const hasVisitedHome = useSelector((state) => state.landing.hasVisitedHome);
  const currentUser = useSelector((state) => state.user.user);

  if (!hasVisitedHome) {
    return <Navigate to={`/${routes.home}`} replace />;
  }

  const currentUserEmail = currentUser?.username || currentUser?.email || "";
  const isGdsUser = isGdsEmail(currentUserEmail);
  const coe = String(roleData?.coe || "").toUpperCase();
  const roles = roleData?.role;
  const isEngagementManager = checkIsEngagementManager(roles);
  const isResourceManager = checkIsResourceManager(roles);
  const isGlobalReportingRole = isGlobalReporting(roles);
  const isCoeMatch = coe === domain;
  const powerBiLink = getPowerBiLink(roles, domain);
  const hasPowerBiAccess =
    powerBiLink !== null && isGdsUser && (isCoeMatch || isGlobalReportingRole);
  const hasRoleAccess =
    isEngagementManager || isResourceManager || hasPowerBiAccess;

  const getDefaultRoute = () => {
    if (isEngagementManager) return routes.engagementManager;
    if (isResourceManager) return routes.resourceManager;
    return null;
  };

  const defaultRoute = getDefaultRoute();

  const makeRedirect = (routeName) =>
    domain === "ADN" ? routeName : `/${domain}/${routeName}`;

  const noAccessPage = (
    <NoAccess powerBiLink={hasPowerBiAccess ? powerBiLink : null} />
  );

  const withGuard = (isAllowed, element) =>
    isAllowed ? element : noAccessPage;

  const hasAccessibleDefault = Boolean(defaultRoute) && isCoeMatch;
  const indexElement = hasAccessibleDefault ? (
    <Navigate to={makeRedirect(defaultRoute)} replace />
  ) : (
    noAccessPage
  );

  if (domain === "ADN") {
    return (
      <Routes>
        <Route index element={indexElement} />
        <Route
          path={routes.viewDetails}
          element={withGuard(hasRoleAccess && isCoeMatch, <ViewDetailsPage />)}
        />
        <Route
          path={routes.resourceManager}
          element={withGuard(
            isResourceManager && isCoeMatch,
            <ResourceManager />,
          )}
        />
        <Route
          path={routes.engagementManager}
          element={withGuard(
            isEngagementManager && isCoeMatch,
            <EngagementManager />,
          )}
        />
        <Route
          path={routes.powerBiDashboard}
          element={withGuard(hasPowerBiAccess, <ADNPowerBiDashboard />)}
        />
        <Route
          path={routes.userManagement}
          element={<Navigate to={`/${routes.userManagement}`} replace />}
        />
      </Routes>
    );
  }

  if (domain === "AOA") {
    return (
      <Routes>
        <Route index element={indexElement} />
        <Route
          path={routes.viewDetails}
          element={withGuard(
            hasRoleAccess && isCoeMatch,
            <AOAViewDetailsPage />,
          )}
        />
        <Route
          path={routes.resourceManager}
          element={withGuard(
            isResourceManager && isCoeMatch,
            <AOAResourceManager />,
          )}
        />
        <Route
          path={routes.engagementManager}
          element={withGuard(
            isEngagementManager && isCoeMatch,
            <AOAEngagementManager />,
          )}
        />
        <Route
          path={routes.powerBiDashboard}
          element={withGuard(hasPowerBiAccess, <AOAPowerBiDashboard />)}
        />
        <Route
          path={routes.userManagement}
          element={<Navigate to={`/${routes.userManagement}`} replace />}
        />
      </Routes>
    );
  }

  return <Navigate to="/" replace />;
};

export default DomainLayout;
